/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useCallback } from 'react';

import { useMediaQuery } from '@/lib/useMediaQuery';
import { useUiStore } from '@/stores/uiStore';

/**
 * From this width the named rail docks beside the page and moves it over. Narrower, it opens over
 * the page instead: every tablet layout is sized for the slim rail, and would reflow around a wide
 * one. Mirrors the rail rules in shell.css.
 */
export const RAIL_DOCK_QUERY = '(min-width: 1280px)';

export interface RailState {
  /** The destinations are named: pinned open on desktop, or opened over the page below it. */
  open: boolean;
  /** The open rail floats over the page rather than moving it. */
  over: boolean;
  /** The arrow at the foot of the rail. */
  toggle: () => void;
}

export function useRail(): RailState {
  const docked = useMediaQuery(RAIL_DOCK_QUERY);
  const pinned = useUiStore((s) => s.railPinned);
  const peek = useUiStore((s) => s.railPeek);
  const setRailPinned = useUiStore((s) => s.setRailPinned);
  const setRailPeek = useUiStore((s) => s.setRailPeek);
  const toggle = useCallback(() => {
    if (docked) setRailPinned(!pinned);
    else setRailPeek(!peek);
  }, [docked, pinned, peek, setRailPinned, setRailPeek]);
  return { open: docked ? pinned : peek, over: !docked && peek, toggle };
}
